/* eslint-disable eqeqeq */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Input,
  message,
} from 'antd';
import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import {
  deleteValorReferidoDePredio,
  getValoresReferidosDePredio,
  patchValorReferidoDePredio,
  postValorReferidoDePredio,
} from 'api/catastro/valoresReferidosDePredios';
import NumericInput from 'components/NumericInput';
import { getPredio, patchPredio, dateKeysPredio } from 'api/catastro/predio';
import removeIndexes from 'views/Tramites/GPM/removeIndexes';
import formatValues from 'utils/formatValues';
import Select from 'components/Select';
import { getDistinctValues } from 'utils/normalizers';

export const permissionConfig = {
  title: 'Valores Referidos De Predios',
  model: 'valorreferidodepredio',
};

const bimestres = [1, 2, 3, 4, 5, 6];

const ValoresReferidosDePredios = ({
  predio = {},
  mutable,
  permission,
  noAfectarPadron,
  callback,
  fetchCallback,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);

  const sortValores = (_data) => {
    const _anios = getDistinctValues(_data.map((e) => e.anio_valor_referido))
      .sort((a, b) => a - b);
    return _anios.map((anio) => _data.filter((e) => e.anio_valor_referido === anio)
      .sort((a, b) => a.bim_valor_referido - b.bim_valor_referido)).flat();
  };

  const fetchData = async () => {
    if (!predio) return;
    setLoading(true);
    if (noAfectarPadron) {
      const _predio = await getPredio(predio.id, false);
      const json = _predio.json_predio ? _predio.json_predio : removeIndexes(_predio);
      json.valores_referidos_de_predios = json.valores_referidos_de_predios
        || await getValoresReferidosDePredio({ predio: predio.id });
      json.valores_referidos_de_predios = sortValores(json.valores_referidos_de_predios);
      json.valores_referidos_de_predios_a_eliminar = json
        .valores_referidos_de_predios_a_eliminar || [];
      json.valores_referidos_de_predios_a_agregar = json
        .valores_referidos_de_predios_a_agregar || [];
      json.valores_referidos_de_predios_a_modificar = json
        .valores_referidos_de_predios_a_modificar || [];
      const json_predio = JSON.stringify(formatValues(json, { clean: false }, dateKeysPredio));
      if (mutable) {
        _predio.json_predio = _predio.json_predio_raw !== json_predio
          ? (await patchPredio(predio.id, { json_predio }, false))?.json_predio
          : json;
      }
      setData(json.valores_referidos_de_predios);
      setLoading(false);
      if (fetchCallback) fetchCallback(json.valores_referidos_de_predios, _predio.json_predio);
      return;
    }
    const _data = await getValoresReferidosDePredio({ predio: predio.id });
    setData(sortValores(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const _fetchData = async () => {
      setLoading(true);
      if (mounted) await fetchData();
    };
    _fetchData();
    return () => { mounted = false; };
  }, [predio.id]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const { aplica_para_terreno, ...values } = form.getFieldsValue();
      values.predio = predio.id;
      values.entidad = 1;
      const [key] = selectedRowKeys;
      const match = data.find((e) => e.id !== key
        && e.bim_valor_referido == values.bim_valor_referido
        && e.anio_valor_referido == values.anio_valor_referido);
      if (match) {
        message.warning(`Ya existe un registro para ${values.anio_valor_referido} Bim ${values.bim_valor_referido}`);
        setLoading(false);
        return;
      }
      if (noAfectarPadron) {
        let json_predio = { ...predio };
        if (key) {
          const idx = json_predio.valores_referidos_de_predios
            .findIndex((e) => e.id === selectedRowKeys[0]);
          json_predio.valores_referidos_de_predios[idx] = {
            ...json_predio.valores_referidos_de_predios[idx],
            ...values,
          };
        } else {
          values.id = `temp_id_${(new Date()).toISOString()}`;
          json_predio.valores_referidos_de_predios.push(values);
        }
        if (typeof key === 'number'
          && !json_predio.valores_referidos_de_predios_a_modificar.includes(key)) {
          json_predio.valores_referidos_de_predios_a_modificar.push(key);
        } else if (!key) {
          json_predio.valores_referidos_de_predios_a_agregar.push(values.id);
        }
        json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
        const saved = await patchPredio(predio.id, { json_predio }, false);
        if (!saved) {
          setLoading(false);
          return;
        }
        if (callback) await callback(`${key ? 'Agregado' : 'Modificado'} correctamente`);
        await fetchData();
        onCancel();
        return;
      }

      const saved = await (key
        ? patchValorReferidoDePredio(key, values) : postValorReferidoDePredio(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const onClickEdit = () => setVisible(true);

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    let deleted = false;
    if (noAfectarPadron) {
      let json_predio = { ...predio };
      json_predio.valores_referidos_de_predios = json_predio.valores_referidos_de_predios
        .filter((e) => e.id !== selectedRowKeys[0]);
      if (typeof key === 'number') {
        json_predio.valores_referidos_de_predios_a_eliminar.push(key);
        if (json_predio.valores_referidos_de_predios_a_modificar.includes(key)) {
          json_predio.valores_referidos_de_predios_a_modificar = json_predio
            .valores_referidos_de_predios_a_modificar.filter((e) => e !== key);
        }
      } else {
        json_predio.valores_referidos_de_predios_a_agregar = json_predio
          .valores_referidos_de_predios_a_agregar.filter((e) => e !== key);
      }
      json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
      deleted = await patchPredio(predio.id, { json_predio }, false);
    } else {
      deleted = await deleteValorReferidoDePredio(key);
    }
    if (deleted) {
      if (callback) await callback('Eliminado correctamente');
      await fetchData();
      setVisibleAlert(false);
      onCancel();
    }
    setLoading(false);
  };

  const rowSelection = mutable ? {
    selectedRowKeys,
    type: 'radio',
  } : null;

  const columns = [
    {
      titleText: 'Año',
      key: 'anio_valor_referido',
      dataIndex: 'anio_valor_referido',
      width: 100,
    },
    {
      titleText: 'Bim',
      key: 'bim_valor_referido',
      dataIndex: 'bim_valor_referido',
      width: 100,
    },
    {
      titleText: 'Valor Total',
      key: 'valor_referido',
      dataIndex: 'valor_referido',
      width: 150,
    },
    {
      titleText: 'Valor Terreno',
      key: 'valor_terreno',
      dataIndex: 'valor_terreno',
      width: 150,
    },
    {
      titleText: 'Valor Construcción',
      key: 'valor_construccion',
      dataIndex: 'valor_construccion',
      width: 150,
    },
  ];

  const rules = {
    required: [{ required: true }],
    valor_referido: [
      {
        validator: async () => {
          const {
            valor_terreno = 0,
            valor_construccion = 0,
            valor_referido = 0,
          } = form.getFieldValue();
          const suma = parseFloat(valor_terreno) + parseFloat(valor_construccion);
          if ((valor_terreno || valor_construccion) && valor_referido != suma) {
            throw new Error('El valor debe ser igual a la suma de valor terreno y valor construcción');
          }
        },
      },
    ],
  };

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <br />
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickDelete,
            onClickEdit,
          }}
          noControls={!mutable}
          allowSort={false}
          allowSearch={false}
          rowKey="id"
          permission={permission}
        />
        <Modal
          open={visible}
          closable={false}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Valor Referido de Predio`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              permission={permission}
            />
          )}
          keyboard={!loading}
          maskClosable={!loading}
          onCancel={onCancel}
          forceRender
        >
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={(changed, all) => {
                if (['valor_terreno', 'valor_construccion'].some((k) => k in changed)) {
                  const { valor_terreno = 0, valor_construccion = 0 } = all;
                  form.setFieldValue('valor_referido', parseFloat(valor_terreno) + parseFloat(valor_construccion));
                }
              }}
              initialValues={{
                valor_terreno: 0,
                valor_construccion: 0,
                valor_referido: 0,
              }}
            >
              <Col span={24}>
                <NumericInput
                  label="Año"
                  name="anio_valor_referido"
                  disabled={!mutable || !!selectedRowKeys.length}
                  rules={rules.required}
                  required
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  label="Bim"
                  name="bim_valor_referido"
                  rules={rules.required}
                >
                  <Select disabled={!mutable || !!selectedRowKeys.length} dataSource={bimestres} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <NumericInput
                  label="Valor Total"
                  name="valor_referido"
                  disabled={!mutable}
                  rules={rules.valor_referido}
                  precision={2}
                  decimal
                  required
                />
              </Col>
              <Col span={24}>
                <NumericInput
                  label="Valor Terreno"
                  name="valor_terreno"
                  disabled={!mutable}
                  precision={2}
                  decimal
                  allowZero
                />
              </Col>
              <Col span={24}>
                <NumericInput
                  label="Valor Construcción"
                  name="valor_construccion"
                  disabled={!mutable}
                  precision={2}
                  decimal
                  allowZero
                />
              </Col>
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="el Valor Referido de Predio seleccionado"
      />
    </>
  );
};

ValoresReferidosDePredios.propTypes = {
  predio: PropTypes.shape(),
  mutable: PropTypes.bool,
  permission: PropTypes.shape().isRequired,
  noAfectarPadron: PropTypes.bool,
  callback: PropTypes.func,
  fetchCallback: PropTypes.func,
};

ValoresReferidosDePredios.defaultProps = {
  predio: null,
  mutable: true,
  noAfectarPadron: false,
  callback: null,
  fetchCallback: null,
};

export default ValoresReferidosDePredios;
